import { Component } from '@theme/component';
import { genderStore } from '@theme/plw-gender';

/**
 * Manages the view state of the PLW slide-in navigation menu:
 * - builds the tablist from the nested tab panels and switches tabs,
 * - slides level-2 submenu panels in/out over the level-1 list,
 * - resets to the first tab whenever the drawer is (re)opened.
 *
 * Open/close of the drawer itself is owned by the ancestor <header-drawer>;
 * this component only manages what's inside it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist - Container the tab buttons are built into.
 * @property {HTMLElement} tabs - Wrapper around the nested tab panels.
 * @property {HTMLElement} body - Scrollable body the submenu panels overlay.
 *
 * @extends {Component<Refs>}
 */
class PlwNavMenu extends Component {
  requiredRefs = ['tablist', 'tabs'];

  /** @type {HTMLElement[]} */
  #tabPanels = [];

  /** @type {HTMLButtonElement[]} */
  #tabButtons = [];

  #activeIndex = 0;

  /** @type {HTMLElement | null} */
  #openPanel = null;

  /** @type {HTMLElement | null} */
  #openTrigger = null;

  /** @type {HTMLDetailsElement | null} */
  #details = null;

  connectedCallback() {
    super.connectedCallback();
    this.#build();
    this.#details = this.closest('details');
    this.#details?.addEventListener('toggle', this.#onToggle);
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#details?.removeEventListener('toggle', this.#onToggle);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#teardownTabButtons();
  }

  // The header section can re-render via the Section Rendering API.
  updatedCallback() {
    this.#build();
  }

  #build() {
    // Only tabs whose menu produced links carry a label; the rest stay empty/hidden.
    const allTabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-nav-tab]')]);
    this.#tabPanels = allTabs.filter((tab) => tab.hasAttribute('data-nav-tab-label'));

    // Take control of the submenu panels from the no-JS `hidden` fallback.
    for (const panel of this.querySelectorAll('[data-nav-panel]')) {
      panel.removeAttribute('hidden');
      this.#deactivatePanel(/** @type {HTMLElement} */ (panel));
    }
    // Clear any submenu-open state left over from a previous session — e.g. the
    // drawer was closed from within a submenu, which leaves the level-1 list /
    // tablist / utility links `inert` and the trigger `aria-expanded="true"`.
    this.#setBackgroundInert(false);
    for (const trigger of this.querySelectorAll('[data-nav-open][aria-expanded="true"]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    this.#openPanel = null;
    this.#openTrigger = null;

    // The tablist replaces the per-tab no-JS headings.
    for (const heading of this.querySelectorAll('[data-nav-tab-heading]')) {
      heading.setAttribute('hidden', '');
    }

    this.#buildTablist();

    if (this.#tabPanels.length) this.#selectTab(this.#preferredTabIndex());
  }

  // The remembered gender preference (plw-gender.js) picks the opening tab:
  // stored "women"/"men" → the tab whose label matches; otherwise the first
  // tab (All). Matched by label like the submenu carry-over — a merchant
  // renaming the tabs degrades this to the plain first-tab default.
  #preferredTabIndex() {
    const gender = genderStore.read()?.gender;
    if (!gender) return 0;
    const index = this.#tabPanels.findIndex(
      (tab) => tab.getAttribute('data-nav-tab-label')?.trim().toLowerCase() === gender
    );
    return index === -1 ? 0 : index;
  }

  // Persist an explicit tab CHOICE (click / arrow key — never #build's restore,
  // which also routes through #selectTab): Women/Men remember that gender,
  // All clears it, any other label leaves the store alone.
  /** @param {number} index */
  #persistTabChoice(index) {
    const label = this.#tabPanels[index]?.getAttribute('data-nav-tab-label')?.trim().toLowerCase();
    if (label === 'women' || label === 'men') genderStore.write(label);
    else if (label === 'all') genderStore.clear();
  }

  #teardownTabButtons() {
    for (const button of this.#tabButtons) {
      button.removeEventListener('click', this.#onTabClick);
      button.removeEventListener('keydown', this.#onTabKeydown);
    }
    this.#tabButtons = [];
  }

  #buildTablist() {
    const { tablist } = this.refs;

    this.#teardownTabButtons();
    tablist.replaceChildren();

    for (const [index, tab] of this.#tabPanels.entries()) {
      const label = tab.getAttribute('data-nav-tab-label') ?? '';
      const panelId = tab.id || `plw-nav-tab-${index}`;
      tab.id = panelId;

      const button = document.createElement('button');
      button.type = 'button';
      button.id = `${panelId}-tab`;
      button.className = 'plw-nav__tab-btn';
      button.textContent = label;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.dataset.tabIndex = String(index);
      button.addEventListener('click', this.#onTabClick);
      button.addEventListener('keydown', this.#onTabKeydown);

      // Point the tabpanel back at its tab so AT announces the relationship.
      tab.setAttribute('aria-labelledby', button.id);

      tablist.appendChild(button);
      this.#tabButtons.push(button);
    }
  }

  /** @param {number} index */
  #selectTab(index) {
    // If a submenu is open, carry it into the target tab: switching Women → Men
    // while viewing "Style" should land on Men's "Style", not Men's top level.
    // Matched by the trigger's label, since the facet submenus share names
    // across the tab menus; no match in the target tab → its top level.
    const carryLabel = this.#openTrigger
      ?.querySelector('.plw-nav__l1-text')
      ?.textContent?.trim()
      .toLowerCase();

    // Don't pull focus back to the old trigger — it's inside the tab that's
    // about to be hidden; focus stays where the caller (tab button) put it.
    this.#closeSubmenu({ focusTrigger: false });
    this.#activeIndex = index;

    for (const [i, tab] of this.#tabPanels.entries()) {
      tab.toggleAttribute('hidden', i !== index);
    }
    for (const [i, button] of this.#tabButtons.entries()) {
      const selected = i === index;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
    }

    if (carryLabel) this.#openMatchingSubmenu(carryLabel);
  }

  /**
   * Re-opens the active tab's submenu whose trigger label matches, if any.
   * Focus is left alone (the user is navigating the tablist).
   * @param {string} label - Trimmed, lowercased trigger label to match.
   */
  #openMatchingSubmenu(label) {
    const activeTab = this.#tabPanels[this.#activeIndex];
    if (!activeTab) return;

    for (const trigger of activeTab.querySelectorAll('[data-nav-open]')) {
      const text = trigger.querySelector('.plw-nav__l1-text')?.textContent?.trim().toLowerCase();
      if (text !== label) continue;

      const panelId = trigger.getAttribute('data-nav-open');
      const panel = panelId && this.querySelector(`#${CSS.escape(panelId)}[data-nav-panel]`);
      if (panel instanceof HTMLElement) this.#openSubmenu(panel, trigger, { focusBack: false });
      return;
    }
  }

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const index = Number(button.dataset.tabIndex);
    this.#selectTab(index);
    this.#persistTabChoice(index);
  };

  // Arrow-key navigation between tabs (left/right/home/end).
  /** @param {KeyboardEvent} event */
  #onTabKeydown = (event) => {
    const last = this.#tabButtons.length - 1;
    if (last < 0) return;

    let next = this.#activeIndex;
    switch (event.key) {
      case 'ArrowRight':
        next = this.#activeIndex >= last ? 0 : this.#activeIndex + 1;
        break;
      case 'ArrowLeft':
        next = this.#activeIndex <= 0 ? last : this.#activeIndex - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectTab(next);
    this.#persistTabChoice(next);
    this.#tabButtons[next]?.focus();
  };

  /**
   * Declarative `on:click` handler on a level-1 parent link.
   * Opens the submenu instead of navigating (progressive enhancement:
   * with JS off the link follows its href to the collection page).
   * @param {MouseEvent} event
   */
  openSubmenu(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-nav-open]');
    const panelId = trigger?.getAttribute('data-nav-open');
    if (!trigger || !panelId) return;

    const panel = this.querySelector(`#${CSS.escape(panelId)}[data-nav-panel]`);
    if (!(panel instanceof HTMLElement)) return;

    event.preventDefault();
    this.#openSubmenu(panel, trigger);
  }

  /**
   * Declarative `on:click` handler on a panel's back button.
   */
  closeSubmenu() {
    this.#closeSubmenu();
  }

  /**
   * @param {HTMLElement} panel
   * @param {Element} trigger
   * @param {{ focusBack?: boolean }} [options] - focusBack: move focus to the
   *   panel's back button (skipped when a tab switch carries a submenu over,
   *   so tablist focus/arrow-key navigation is not hijacked).
   */
  #openSubmenu(panel, trigger, { focusBack = true } = {}) {
    if (this.#openPanel && this.#openPanel !== panel) this.#deactivatePanel(this.#openPanel);

    this.#openPanel = panel;
    this.#openTrigger = /** @type {HTMLElement} */ (trigger);

    // The panels are `inset: 0` inside the scrolling body, i.e. anchored to
    // its scroll-ZERO coordinates — any body scroll misaligns every pane.
    // Worse, the off-canvas panel's translated box extends the body's
    // scrollable overflow a full panel-width to the RIGHT, and iOS scrolls
    // focused elements into view even through `overflow: hidden` — focusing
    // the back button mid-slide horizontally scrolled the body by whatever
    // distance the panel had left to travel (the "random missing padding"
    // offsets on device). So: park the body at 0, FREEZE it (`--frozen` →
    // `overflow: clip`, not a scroll container at all) while a panel is up,
    // and never let programmatic focus scroll. Close restores the L1 spot.
    const body = this.querySelector('.plw-nav__body');
    if (body) {
      this.#bodyScrollBeforeSubmenu = body.scrollTop;
      body.scrollTop = 0;
      body.scrollLeft = 0;
      body.classList.add('plw-nav__body--frozen');
    }
    const items = panel.querySelector('.plw-nav-panel__items');
    if (items) items.scrollTop = 0;

    panel.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    panel.removeAttribute('inert');
    trigger.setAttribute('aria-expanded', 'true');

    this.#setBackgroundInert(true);

    const back = panel.querySelector('.plw-nav-panel__back');
    if (focusBack && back instanceof HTMLElement) {
      requestAnimationFrame(() => back.focus({ preventScroll: true }));
    }
  }

  /**
   * @param {{ focusTrigger?: boolean }} [options] - focusTrigger: return focus
   *   to the link that opened the submenu (skipped on tab switches, where that
   *   link is inside the tab being hidden).
   */
  #closeSubmenu({ focusTrigger = true } = {}) {
    const panel = this.#openPanel;
    if (!panel) return;

    this.#deactivatePanel(panel);
    this.#openPanel = null;
    this.#setBackgroundInert(false);

    // Unfreeze and return the L1 view exactly where the user left it.
    this.#restoreBodyScroll();

    const trigger = this.#openTrigger;
    this.#openTrigger = null;
    trigger?.setAttribute('aria-expanded', 'false');
    if (focusTrigger) trigger?.focus?.({ preventScroll: true });
  }

  /** Body scroll position captured when a submenu opened. */
  #bodyScrollBeforeSubmenu = 0;

  // Lift the `--frozen` scroll lock and re-park the body on the saved L1
  // offset (clip → auto resets scroll state, so restore AFTER unfreezing).
  #restoreBodyScroll() {
    const body = this.querySelector('.plw-nav__body');
    if (!body) return;
    body.classList.remove('plw-nav__body--frozen');
    body.scrollTop = this.#bodyScrollBeforeSubmenu;
    body.scrollLeft = 0;
  }

  /** @param {HTMLElement} panel */
  #deactivatePanel(panel) {
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
  }

  // Hide the level-1 list + utility links (the body content the panel covers)
  // from AT + tab order while a submenu is open. The tablist stays active: the
  // tabs remain visible across the top, and clicking one routes through
  // #selectTab → #closeSubmenu, returning to the top level.
  /** @param {boolean} on */
  #setBackgroundInert(on) {
    const targets = this.querySelectorAll('[data-nav-l1], [data-nav-utility]');
    for (const element of targets) {
      if (element instanceof HTMLElement) element.toggleAttribute('inert', on);
    }
  }

  // Esc closes an open submenu first; only then does it fall through to
  // <header-drawer>, which closes the whole drawer.
  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#openPanel) return;

    this.#closeSubmenu();
    event.stopPropagation();
  };

  // Rebuild from the live DOM each time the drawer opens. This doubles as the
  // "reset to first tab" behavior and guarantees a correct tablist even when the
  // header was re-rendered after connect (Section Rendering API / Horizon's
  // eager-loading second pass), which can leave connectedCallback's initial
  // #build() having run before the tab panels were in place.
  #onToggle = () => {
    if (this.#details?.open) {
      this.#build();
    } else {
      // Closing from inside a submenu must release the inert/aria state that
      // opening it set, or the next open finds the whole menu inert. Reset
      // without focusing the trigger — the drawer is on its way out.
      this.#resetSubmenuState();
    }
  };

  // Tear down submenu-open state without moving focus. Used when the drawer
  // itself closes; #closeSubmenu is the focus-returning variant for the back button.
  #resetSubmenuState() {
    if (this.#openPanel) this.#deactivatePanel(this.#openPanel);
    this.#openTrigger?.setAttribute('aria-expanded', 'false');
    this.#openPanel = null;
    this.#openTrigger = null;
    this.#setBackgroundInert(false);
    this.#restoreBodyScroll();
  }
}

if (!customElements.get('plw-nav-menu')) {
  customElements.define('plw-nav-menu', PlwNavMenu);
}
